import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ApartmentOutlined,
  AreaChartOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DownOutlined,
  ExperimentOutlined,
  KeyOutlined,
  LogoutOutlined,
  ProjectOutlined,
  RocketOutlined,
  SafetyOutlined,
  ScheduleOutlined,
  SettingOutlined,
  TableOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { WxbDropdown } from '../wxb-ui/Dropdown/Dropdown';
import { WxbButton } from '../wxb-ui/Button/Button';
import { WxbTag } from '../wxb-ui/Tag/Tag';
import { WxbAvatar } from '../wxb-ui/Avatar/Avatar';
import { wxbToast } from '../wxb-ui/Toast/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { isAuthEnforced } from '../auth/ProtectedRoute';
import {
  canSeeNavigationItem,
  getGroupSelection,
  isPlatformControlPlanePath,
  navGroups,
  type NavGroup,
  type NavIconKey,
} from './navigationModel';
import './TopNavigation.css';
import {
  replaceSiteInPagePath,
  stripSitePagePrefix,
  toSitePagePath,
} from '../../siteRoutes';

const NAV_ICONS: Record<NavIconKey, React.ReactNode> = {
  apartment: <ApartmentOutlined />,
  areaChart: <AreaChartOutlined />,
  clock: <ClockCircleOutlined />,
  dashboard: <DashboardOutlined />,
  experiment: <ExperimentOutlined />,
  key: <KeyOutlined />,
  project: <ProjectOutlined />,
  rocket: <RocketOutlined />,
  safety: <SafetyOutlined />,
  schedule: <ScheduleOutlined />,
  setting: <SettingOutlined />,
  table: <TableOutlined />,
  team: <TeamOutlined />,
  userSwitch: <UserSwitchOutlined />,
};

const getAvatarInitials = (
  displayName?: string,
  username?: string,
): string => {
  const accountInitials = username
    ?.trim()
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 2)
    .toUpperCase();
  if (accountInitials) return accountInitials;

  const name = displayName?.trim();
  if (!name) return 'APS';
  return /[一-龥]/.test(name)
    ? name.slice(0, 2)
    : name.slice(0, 2).toUpperCase();
};

export default function TopNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    memberships,
    currentSite,
    selectSite,
    logout,
    hasPermission,
    hasPlatformPermission,
  } = useAuth();
  const [siteSwitching, setSiteSwitching] = useState(false);
  const activeMemberships = useMemo(
    () => memberships.filter((membership) => membership.membershipStatus === 'ACTIVE'),
    [memberships],
  );

  const handleSiteSwitch = useCallback(async (sitePublicId: string) => {
    if (siteSwitching || sitePublicId === currentSite?.publicId) {
      return;
    }

    setSiteSwitching(true);
    try {
      await selectSite(sitePublicId);
      if (location.pathname.startsWith('/sites/')) {
        navigate(
          `${replaceSiteInPagePath(location.pathname, sitePublicId)}${location.search}${location.hash}`,
          { replace: true },
        );
      }
    } catch (error) {
      wxbToast.error(error instanceof Error ? error.message : 'Site 切换失败');
    } finally {
      setSiteSwitching(false);
    }
  }, [
    currentSite?.publicId,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    selectSite,
    siteSwitching,
  ]);

  // 按权限过滤导航：隐藏无权限的菜单项；过滤后子项为空的分组整组隐藏。
  // 显式兼容模式（REACT_APP_AUTH_ENFORCE=false）下匿名用户放行全部菜单，
  // 与 ProtectedRoute 的 allowAnonymousInShadow 语义对齐——避免未登录的现有前端看到空导航。
  // 一旦登录（即便仍在影子模式），便按权限过滤，与路由守卫对 requiredPermission 的把关一致。
  const canSee = useCallback(
    (requiredPermission?: string, requiredPlatformPermission?: string) => canSeeNavigationItem(
      requiredPermission,
      Boolean(user),
      isAuthEnforced(),
      hasPermission,
      requiredPlatformPermission,
      hasPlatformPermission,
    ),
    [user, hasPermission, hasPlatformPermission],
  );

  const visibleGroups = useMemo(
    () =>
      navGroups.reduce<NavGroup[]>((acc, group) => {
        if (group.children) {
          const children = group.children.filter((child) => canSee(
            child.requiredPermission,
            child.requiredPlatformPermission,
          ));
          if (children.length > 0) {
            acc.push({ ...group, children });
          }
          return acc;
        }
        if (canSee(group.requiredPermission)) {
          acc.push(group);
        }
        return acc;
      }, []),
    [canSee],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);
  const navRef = useRef<HTMLElement | null>(null);
  const linksRef = useRef<HTMLDivElement | null>(null);
  const linkRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, minWidth: 220 });

  const { group: activeGroup, child: activeChild } = useMemo(
    () => getGroupSelection(stripSitePagePrefix(location.pathname), visibleGroups),
    [location.pathname, visibleGroups],
  );

  const movePill = useCallback((key: string) => {
    const links = linksRef.current;
    const target = linkRefs.current[key];

    if (!links || !target) {
      setPillStyle((current) => ({ ...current, opacity: 0 }));
      return;
    }

    const linksRect = links.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    setPillStyle({
      left: targetRect.left - linksRect.left,
      width: targetRect.width,
      opacity: 1,
    });
  }, []);

  const openMenu = useCallback((key: string) => {
    const target = linkRefs.current[key];

    if (target) {
      const rect = target.getBoundingClientRect();
      setMenuPosition({
        left: rect.left,
        top: rect.bottom + 8,
        minWidth: Math.max(220, rect.width),
      });
    }

    setOpenGroupKey(key);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => movePill(activeGroup?.key ?? ''));
    const handleResize = () => {
      movePill(activeGroup?.key ?? '');

      if (openGroupKey) {
        const target = linkRefs.current[openGroupKey];
        if (target) {
          const rect = target.getBoundingClientRect();
          setMenuPosition({
            left: rect.left,
            top: rect.bottom + 8,
            minWidth: Math.max(220, rect.width),
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeGroup?.key, movePill, openGroupKey]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Node && navRef.current?.contains(target)) {
        return;
      }

      setOpenGroupKey(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenGroupKey(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      const target = isPlatformControlPlanePath(path) || !currentSite
        ? path
        : toSitePagePath(currentSite.publicId, path);
      navigate(target);
      setOpenGroupKey(null);
    },
    [currentSite, navigate],
  );

  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty('--my', `${event.clientY - rect.top}px`);
  };

  return (
    <header className="wxb-topnav-shell">
      <nav
        aria-label="主导航"
        className="wxb-topnav"
        onMouseMove={handleMouseMove}
        ref={navRef}
      >
        <button
          aria-label="返回运营总览"
          className="wxb-topnav-logo"
          onClick={() => handleNavigate('/operations-overview')}
          type="button"
        >
          <span className="wxb-topnav-mark">
            <img alt="" src="/wuxibio-icon.svg" />
          </span>
          <span className="wxb-topnav-name">
            <span className="wxb-topnav-name-main">MFG8 APS</span>
            <span className="wxb-topnav-name-sub">WuXi Biologics</span>
          </span>
        </button>

        <div
          className="wxb-topnav-links"
          onMouseLeave={() => movePill(activeGroup?.key ?? '')}
          ref={linksRef}
        >
          <span
            aria-hidden="true"
            className="wxb-topnav-pill"
            style={{
              left: pillStyle.left,
              opacity: pillStyle.opacity,
              width: pillStyle.width,
            }}
          />

          {visibleGroups.map((item) => {
            const active = item.key === activeGroup?.key;
            const childItems = item.children;
            const menuOpen = openGroupKey === item.key;

            const linkButton = (
              <button
                aria-current={active ? 'page' : undefined}
                aria-expanded={childItems ? menuOpen : undefined}
                aria-haspopup={childItems ? 'menu' : undefined}
                className={`wxb-topnav-link${active ? ' is-active' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();

                  if (item.path) {
                    handleNavigate(item.path);
                    return;
                  }

                  openMenu(item.key);
                }}
                onFocus={() => movePill(item.key)}
                onKeyDown={(event) => {
                  if (childItems && event.key === 'ArrowDown') {
                    event.preventDefault();
                    openMenu(item.key);
                  }
                }}
                onMouseEnter={() => movePill(item.key)}
                ref={(node) => {
                  linkRefs.current[item.key] = node;
                }}
                type="button"
              >
                <span className="wxb-topnav-link-icon">{NAV_ICONS[item.icon]}</span>
                <span className="wxb-topnav-link-copy">
                  <span className="wxb-topnav-link-label">{item.label}</span>
                  <span className="wxb-topnav-link-subtitle">{item.subtitle}</span>
                </span>
                {childItems ? <DownOutlined className="wxb-topnav-link-caret" /> : null}
              </button>
            );

            return childItems ? (
              <div
                className="wxb-topnav-menu-wrap"
                key={item.key}
                onBlur={(event) => {
                  const nextFocus = event.relatedTarget;
                  if (!(nextFocus instanceof Node) || !event.currentTarget.contains(nextFocus)) {
                    setOpenGroupKey(null);
                  }
                }}
              >
                {linkButton}
                {menuOpen ? (
                  <div
                    className="wxb-topnav-menu"
                    role="menu"
                    style={{
                      left: menuPosition.left,
                      minWidth: menuPosition.minWidth,
                      top: menuPosition.top,
                    }}
                  >
                    <div className="wxb-topnav-menu-title">{item.label}</div>
                    {childItems.map((child) => {
                      const childActive = activeChild?.key === child.key;

                      return (
                        <button
                          className={`wxb-topnav-menu-item${childActive ? ' is-active' : ''}`}
                          key={child.key}
                          onClick={() => handleNavigate(child.path)}
                          role="menuitem"
                          type="button"
                        >
                          <span className="wxb-topnav-menu-icon">{NAV_ICONS[child.icon]}</span>
                          <span>{child.label}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : (
              <React.Fragment key={item.key}>{linkButton}</React.Fragment>
            );
          })}
        </div>

        <div className="wxb-topnav-spacer" />

        <div className="wxb-topnav-right">
          {currentSite && activeMemberships.length > 1 ? (
            <WxbDropdown
              disabled={siteSwitching}
              menu={{
                items: activeMemberships.map((membership) => ({
                  key: membership.site.publicId,
                  disabled: membership.site.publicId === currentSite.publicId,
                  label: `${membership.site.siteCode} · ${membership.site.siteName}`,
                  onClick: () => {
                    void handleSiteSwitch(membership.site.publicId);
                  },
                })),
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <WxbButton
                aria-label={`切换 Site，当前：${currentSite.siteCode} ${currentSite.siteName}`}
                className="wxb-topnav-site wxb-topnav-site-switcher"
                disabled={siteSwitching}
                size="sm"
                variant="ghost"
              >
                <span className="wxb-topnav-site-label">当前 Site</span>
                <WxbTag className="wxb-topnav-site-code" color="blue">
                  {currentSite.siteCode}
                </WxbTag>
                <span className="wxb-topnav-site-name">{currentSite.siteName}</span>
                <DownOutlined className="wxb-topnav-site-caret" />
              </WxbButton>
            </WxbDropdown>
          ) : currentSite ? (
            <div
              aria-label={`当前 Site：${currentSite.siteCode} ${currentSite.siteName}`}
              className="wxb-topnav-site"
            >
              <span className="wxb-topnav-site-label">当前 Site</span>
              <WxbTag className="wxb-topnav-site-code" color="blue">
                {currentSite.siteCode}
              </WxbTag>
              <span className="wxb-topnav-site-name">{currentSite.siteName}</span>
            </div>
          ) : null}
          <WxbDropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'user-info',
                  label: (
                    <div style={{ padding: '4px 0', lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 600, color: 'var(--wx-fg-1)' }}>
                        {user?.displayName || '未登录'}
                      </div>
                      {user?.username ? (
                        <div style={{ fontSize: 'var(--wx-fs-12)', color: 'var(--wx-fg-3)' }}>
                          @{user.username}
                        </div>
                      ) : null}
                    </div>
                  ),
                  disabled: true,
                },
                { type: 'divider' as const },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  onClick: handleLogout,
                },
              ],
            }}
          >
            <WxbButton
              aria-label={`当前用户：${user?.displayName || '未登录'}`}
              className="wxb-topnav-avatar"
              size="sm"
              type="button"
              variant="ghost"
            >
              <WxbAvatar
                aria-hidden="true"
                initials={getAvatarInitials(user?.displayName, user?.username)}
                size={34}
              />
              <span className="wxb-topnav-presence" />
            </WxbButton>
          </WxbDropdown>
        </div>
      </nav>
    </header>
  );
}
